import { filterDOMProps, isMac } from '@react-aria/utils';
import { isString } from 'lodash-es';
import type { ReactNode } from 'react';
import { createContext, forwardRef, useMemo } from 'react';
import type { ContextValue } from 'react-aria-components';
import { useContextProps } from 'react-aria-components';

import { cn, styleVars } from '@infinex/ui-styles';

import type { SlotComponentProps } from './Content';

export type KbdProps = {
  /** Keyboard shortcut text. */
  children?: ReactNode;
  /**
   * Prefix with OS-specific "alt" key.
   * @MacOS `⎇`
   * @Windows `Alt`
   */
  alt?: boolean;
  /**
   * Prefix with OS-specific "meta" key.
   * @MacOS `⌘`
   * @Windows `Ctrl`
   */
  meta?: boolean;
  /**
   * Prefix with OS-specific "shift" key.
   * @MacOS `⇧`
   * @Windows `Shift`
   */
  shift?: boolean;
} & SlotComponentProps;

export const KbdContext = createContext<ContextValue<KbdProps, HTMLElement>>(
  {}
);

/**
 * The `Kbd` primitive represents text that specifies a keyboard command.
 */
export const Kbd = forwardRef<HTMLElement, KbdProps>(function Kbd(props, ref) {
  [props, ref] = useContextProps(props, ref, KbdContext);
  const { alt, meta, shift, children, slot, ...otherProps } = props;

  const modifiers = useMemo(() => {
    const SYSTEM_KEYS = isMac()
      ? {
          alt: '⌥',
          meta: '⌘',
          shift: '⇧',
        }
      : {
          alt: 'Alt',
          meta: 'Ctrl',
          shift: 'Shift',
        };
    const keys = [
      alt && SYSTEM_KEYS.alt,
      shift && SYSTEM_KEYS.shift,
      meta && SYSTEM_KEYS.meta,
    ].filter(isString);

    const delimiter = isMac() ? '' : '+';
    return joinModifierKeys(keys, delimiter);
  }, [alt, meta, shift]);

  return (
    <kbd
      {...filterDOMProps(otherProps)}
      ref={ref}
      slot={slot || undefined}
      className={cn(
        'inline-block bg-(--clr) px-(--gutter) font-sans leading-snug',
        props.className
      )}
      style={styleVars({
        // Use "currentColor" to support inverse rendering contexts
        '--clr': 'color-mix(in srgb, currentColor 16%, transparent)',
        '--gutter': '0.5ch',
        boxShadow: 'inset 0 -2px 0 0 var(--clr)',
        borderRadius: 2,
        ...props.style,
      })}
    >
      {modifiers && <span>{modifiers}</span>}
      {/*
        The "full-width" text-transform has limited support:
        https://developer.mozilla.org/en-US/docs/Web/CSS/text-transform#browser_compatibility

        This approach ensures that single character shortcuts are visually aligned when
        stacked, like when end-aligned in menu items, without needing to use a
        monospace font.
       */}
      <span className="inline-block min-w-[1em] text-center">{children}</span>
    </kbd>
  );
});

// Helpers ------------------------------------------------------------

function joinModifierKeys(modifiers: string[], delimiter: string) {
  if (modifiers.length === 0) {
    return '';
  }

  return modifiers.join(delimiter) + delimiter;
}
